import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import { setSortCompanies, searchSortSelector, companiesWorkSpaceTypeSelector } from 'domain/companies';
import { unreadRequestsLoadedSelector } from 'domain/documents/documentSelector';
import { messagesLoadedSelector } from 'domain/chat/chatSelector';
import { isCurrentOrganizationSetSelector, userHasMultipleOrgsSelector } from 'domain/organization';
import { isDokkaSupportSelector } from 'domain/env/envSelector';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from 'components/mui/Tooltip';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import GhostTabs from 'components/mui/Layouts/components/GhostTabs';
import WorkspaceViewToggle from 'pages/companies/components/Bar/components/WorkspaceViewToggle';
import ExportButton from 'pages/companies/components/Bar/components/ExportButton';

type BarProps = {
  exportToExcel: () => void;
};

type ChipItem = {
  key: string;
  label: string;
  disabled: boolean;
  hidden: boolean;
  tooltip?: string;
};

const Bar: React.FC<BarProps> = ({ exportToExcel }) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const sort = useSelector(searchSortSelector);
  const messagesLoaded = useSelector(messagesLoadedSelector);
  const unreadRequestsLoaded = useSelector(unreadRequestsLoadedSelector);
  const isCurrentOrganizationSet = useSelector(isCurrentOrganizationSetSelector);
  const isMultiplyOrganizations = useSelector(userHasMultipleOrgsSelector);
  const isGhost = useSelector(isDokkaSupportSelector);
  const viewType = useSelector(companiesWorkSpaceTypeSelector);
  const isGridView = viewType === 'grid';

  const onClick = (key: string | null) => {
    const filter = sort === key ? null : key;
    return dispatch(setSortCompanies(filter));
  };

  const chips = useMemo<ChipItem[]>(
    () => [
      {
        key: 'favorites',
        label: formatMessage({
          id: 'companies.bar.favorites',
          defaultMessage: 'Favorites',
        }),
        disabled: false,
        hidden: isGridView,
      },
      {
        key: 'unread',
        label: formatMessage({
          id: 'companies.bar.unread',
          defaultMessage: 'Unread Messages',
        }),
        disabled: !unreadRequestsLoaded || !messagesLoaded,
        hidden: isGhost || isGridView,
        tooltip:
          !unreadRequestsLoaded || !messagesLoaded
            ? formatMessage({
                id: 'companies.bar.disabledSortingByMessage',
                defaultMessage: 'Loading messages. Please wait',
              })
            : undefined,
      },
      {
        key: 'new',
        label: formatMessage({
          id: 'companies.bar.new',
          defaultMessage: 'New Documents',
        }),
        disabled: false,
        hidden: isGridView,
      },
      {
        key: 'pendingApproval',
        label: formatMessage({
          id: 'companies.bar.pendingApproval',
          defaultMessage: 'Pending Approval',
        }),
        hidden: isGhost || isGridView,
        disabled: false,
      },
    ],
    [formatMessage, isGhost, unreadRequestsLoaded, messagesLoaded, isGridView],
  );

  const actualChips = useMemo(() => chips.filter(({ hidden }) => !hidden), [chips]);

  return (
    <Box display="flex" alignItems="center" mt={2}>
      {Boolean(actualChips.length) && (
        <Typography variant="subtitle2" mr={2}>
          {formatMessage({ id: 'companies.bar.title', defaultMessage: 'Sort by' })}
        </Typography>
      )}
      <Stack direction="row" spacing={1}>
        {actualChips.map((chip) => (
          <Tooltip key={chip.key} t={chip.tooltip}>
            <Chip
              label={chip.label}
              onClick={() => onClick(chip.key)}
              disabled={chip.disabled}
              color={chip.key === sort ? 'primary' : 'default'}
            />
          </Tooltip>
        ))}
      </Stack>

      <Stack direction="row" ml="auto" spacing={2} alignItems="center">
        {isGridView && <ExportButton onClick={exportToExcel} />}
        {!isCurrentOrganizationSet && isMultiplyOrganizations && <GhostTabs />}
        <WorkspaceViewToggle />
      </Stack>
    </Box>
  );
};

export default Bar;
